import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BookOpen,
  ChevronRight,
  Clock,
  ExternalLink,
  LogOut,
  MessageCircle,
  Package,
  Radio,
  Store,
  User,
} from 'lucide-react';
import DocsCarousel from './DocsCarousel';

const SHOW_TIENDA = false;

function Options({ userId, username, setActiveComponent, onLogout }) {
  const [userSummary, setUserSummary] = useState({
    username: username || 'Usuario',
    profileImage: null,
  });

  useEffect(() => {
    let isMounted = true;

    async function fetchUserSummary() {
      if (!userId) return;

      const token = localStorage.getItem('token');

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user_edit/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!isMounted) return;

        setUserSummary({
          username: response.data.user?.username || username || 'Usuario',
          profileImage: response.data.user?.profile_image || null,
        });
      } catch (error) {
        if (!isMounted) return;

        // The menu must remain usable even when the optional profile request fails.
        console.error('Error fetching user summary:', error);
        setUserSummary((current) => ({
          ...current,
          username: username || current.username || 'Usuario',
        }));
      }
    }

    fetchUserSummary();

    return () => {
      isMounted = false;
    };
  }, [userId, username]);

  function handleInstructionsClick() {
    window.open('/#/user-docs', '_blank', 'noopener,noreferrer');
  }

  const menuItems = useMemo(
    () => [
      {
        id: 'Analytics',
        label: 'Mis Entregas',
        description: 'Consulta tus envíos y su estado',
        icon: Clock,
        color: 'from-[#B1336E] to-[#D55389]',
      },
      {
        id: 'ControlTower',
        label: 'Torre de Control',
        description: 'Visibilidad de tu operación en tiempo real',
        icon: Radio,
        color: 'from-[#67A43C] to-[#8BBB55]',
      },
      {
        id: 'Chat',
        label: 'Chat Fast Max',
        description: 'Comunícate con nuestro equipo',
        icon: MessageCircle,
        color: 'from-[#B1336E] to-[#67A43C]',
      },
      {
        id: 'Inventory',
        label: 'Inventario',
        description: 'Controla tus productos y paquetes',
        icon: Package,
        color: 'from-[#E6B43C] to-[#CF8D2D]',
      },
      {
        id: 'ProviderCatalog',
        label: 'Tienda',
        description: 'Explora el catálogo Fast Max',
        icon: Store,
        color: 'from-[#67A43C] to-[#E6B43C]',
        visible: SHOW_TIENDA,
      },
    ],
    []
  );

  const visibleMenuItems = menuItems.filter((item) => item.visible !== false);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-[#B1336E]/5">
      <main className="no-scrollbar flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
          <button
            type="button"
            onClick={() => setActiveComponent('MyAccount')}
            className="group mb-6 w-full overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#B1336E]/25 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2 sm:p-5"
            aria-label="Abrir Mi cuenta"
          >
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-2xl bg-gradient-to-br from-[#B1336E] to-[#E6B43C] shadow-md ring-4 ring-[#B1336E]/5 sm:h-20 sm:w-20">
                {userSummary.profileImage ? (
                  <img
                    src={userSummary.profileImage}
                    alt={`Foto de perfil de ${userSummary.username}`}
                    className="h-full w-full object-cover"
                    onError={() =>
                      setUserSummary((current) => ({ ...current, profileImage: null }))
                    }
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <User className="h-8 w-8 text-white sm:h-10 sm:w-10" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em] text-slate-400 font-montserrat">
                  Mi cuenta
                </p>
                <h1 className="truncate text-xl font-extrabold text-slate-900 font-montserrat sm:text-2xl">
                  {userSummary.username}
                </h1>
                <p className="mt-1 text-sm text-slate-500 font-montserrat">
                  Perfil, referidos e integraciones
                </p>
              </div>

              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition-all duration-200 group-hover:bg-[#B1336E]/10 group-hover:text-[#B1336E]">
                <ChevronRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </div>
            </div>
          </button>

          <section aria-labelledby="operations-title" className="mb-7">
            <div className="mb-3 flex items-end justify-between gap-3 px-1">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#B1336E] font-montserrat">
                  Operación
                </p>
                <h2 id="operations-title" className="mt-1 text-xl font-extrabold text-slate-900 font-montserrat">
                  ¿Qué deseas hacer?
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              {visibleMenuItems.map((item) => {
                const Icon = item.icon;

                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => setActiveComponent(item.id)}
                    className="group rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#B1336E]/20 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2 sm:p-5"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${item.color} text-white shadow-md`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-900 transition-colors duration-200 group-hover:text-[#B1336E] font-montserrat sm:text-lg">
                          {item.label}
                        </p>
                        <p className="mt-0.5 text-xs leading-5 text-slate-500 font-montserrat sm:text-sm">
                          {item.description}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 flex-shrink-0 text-slate-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#B1336E]" />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <DocsCarousel role="customer" />

          <button
            type="button"
            onClick={handleInstructionsClick}
            className="group mt-5 flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-left shadow-sm transition-all duration-200 hover:border-[#B1336E]/25 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#B1336E]/10 text-[#B1336E]">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-slate-800 font-montserrat">Instrucciones</p>
              <p className="truncate text-xs text-slate-500 font-montserrat sm:text-sm">
                Consulta las guías de uso de Fast Max
              </p>
            </div>
            <ExternalLink className="h-5 w-5 flex-shrink-0 text-slate-400 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#B1336E]" />
          </button>
        </div>
      </main>

      <footer className="flex-shrink-0 border-t border-slate-200/80 bg-white/95 shadow-[0_-8px_24px_rgba(15,23,42,0.04)] backdrop-blur">
        <div className="mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-3 font-semibold text-red-600 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 font-montserrat"
          >
            <LogOut className="h-5 w-5" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </footer>
    </div>
  );
}

export default Options;
